import { AreaSeries, createChart, LineSeries, type UTCTimestamp } from "lightweight-charts"
import { useEffect, useRef } from "react"
import { CHART_OPTIONS } from "../chart/chartTheme.ts"
import { timeFormatters } from "../chart/chartTime.ts"

/** 1.25M, 980K: equity reaches millions, and the axis has little room. */
const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 })

/** The equity curve of a backtest against buy & hold, as in TradingView's Overview. */
export function EquityChart({ barTimes, buyHold, equity, initialCapital, intraday }: {
  readonly equity: ReadonlyArray<number>
  /** Buy & hold profit per bar (the report's), drawn as equity. */
  readonly buyHold: ReadonlyArray<number>
  readonly initialCapital: number
  readonly barTimes: ReadonlyArray<number>
  readonly intraday: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const { tickMarkFormatter, timeFormatter } = timeFormatters(intraday)
    const chart = createChart(containerRef.current!, {
      ...CHART_OPTIONS,
      localization: { timeFormatter, priceFormatter: (price: number) => compact.format(price) },
      timeScale: { borderColor: "#2a2e39", timeVisible: intraday, tickMarkFormatter }
    })
    const points = (values: ReadonlyArray<number>, offset: number) =>
      values.flatMap((value, i) => {
        const time = barTimes[i]
        return time === undefined || !Number.isFinite(value) ? [] : [{ time: time as UTCTimestamp, value: value + offset }]
      })
    chart.addSeries(AreaSeries, {
      lineColor: "#2962ff",
      topColor: "rgba(41, 98, 255, 0.35)",
      bottomColor: "rgba(41, 98, 255, 0.02)",
      lineWidth: 2,
      title: "Equity"
    }).setData(points(equity, 0))
    chart.addSeries(LineSeries, { color: "#787b86", lineWidth: 1, lineStyle: 2, title: "Buy & hold" })
      .setData(points(buyHold, initialCapital))
    chart.timeScale().fitContent()
    return () => chart.remove()
  }, [equity, buyHold, initialCapital, barTimes, intraday])

  return <div ref={containerRef} className="equity-chart" />
}
